import { useEffect, useState } from "react";
import { Navigate, Route, Routes, useParams } from "react-router-dom";

import { AdminAccessGate, canAuthorKb, isIquestAdmin } from "./components/AdminAccessGate";
import { AdminLayout } from "./components/AdminLayout";
import { AppShell } from "./components/AppShell";
import { ServiceHubLayout } from "./components/ServiceHubLayout";
import { features } from "./config/features";
import { initAuth, currentAccount } from "./auth";
import { AdminAnnouncements } from "./pages/AdminAnnouncements";
import { AdminStatusAlertComposer } from "./pages/AdminStatusAlertComposer";
import { AdminFormEdit } from "./pages/AdminFormEdit";
import { AdminForms } from "./pages/AdminForms";
import { AdminKB } from "./pages/AdminKB";
import { AdminKBEdit } from "./pages/AdminKBEdit";
import { AdminClientNew } from "./pages/AdminClientNew";
import { AdminClients } from "./pages/AdminClients";
import { AdminCustomer } from "./pages/AdminCustomer";
import { AdminHome } from "./pages/AdminHome";
import { AdminUsers } from "./pages/AdminUsers";
import { AdminViewAs } from "./pages/AdminViewAs";
import { Approvals } from "./pages/Approvals";
import { Dashboard } from "./pages/Dashboard";
import { FormSubmit } from "./pages/FormSubmit";
import { KbLayout } from "./components/KbLayout";
import { KbBrowse } from "./pages/KbBrowse";
import { KbCategories } from "./pages/KbCategories";
import { KbCategory } from "./pages/KbCategory";
import { KbRecent } from "./pages/KbRecent";
import { KbSaved } from "./pages/KbSaved";
import { KbSearch } from "./pages/KbSearch";
import { KBArticle } from "./pages/KBArticle";
import { MePreferences } from "./pages/MePreferences";
import { MyDelegation } from "./pages/MyDelegation";
import { MyRequests } from "./pages/MyRequests";
import { ReportingDashboard } from "./reporting/ReportingDashboard";
import { ServiceCatalog } from "./pages/ServiceCatalog";
import { SignIn } from "./pages/SignIn";
import { SLA } from "./pages/SLA";
import { Tickets } from "./pages/Tickets";
import { Trust } from "./pages/Trust";

const CUSTOMERS_BLURB = "Managing customers is reserved for iQuest admins.";
const KB_ADMIN_BLURB = "Knowledge base editing is reserved for iQuest staff and KB authors.";

export function App() {
  const [booted, setBooted] = useState(false);
  const [signedIn, setSignedIn] = useState(false);

  useEffect(() => {
    initAuth().then(() => {
      setSignedIn(currentAccount() !== null);
      setBooted(true);
    });
  }, []);

  if (!booted) {
    return <div className="boot-splash">Loading iQuest Portal…</div>;
  }

  if (!signedIn) {
    return (
      <Routes>
        <Route path="*" element={<SignIn />} />
      </Routes>
    );
  }

  return (
    <AppShell>
      <Routes>
        <Route path="/" element={<Dashboard />} />
        <Route path="/tickets" element={<Tickets />} />
        {/* Deep link to a single ticket (from the Dashboard recent list, SLA
            page, etc.). Renders the same Tickets page, which reads the param
            and opens the detail modal; closing it navigates back to /tickets. */}
        <Route path="/tickets/:ticketNumber" element={<Tickets />} />
        {/* Section routes gated by the same build-time flags as their nav
            items (src/config/features.ts). When a flag is off the route is
            absent, so a direct URL hits the catch-all redirect to "/". */}
        {features.sla && <Route path="/sla" element={<SLA />} />}
        {/* Reporting engine (BrightGauge replacement). Gated by the same
            default-off build flag as its nav item; the page itself re-checks the
            server gate (reporting_enabled + view access) and shows an access
            notice if the caller may not view. */}
        {features.reporting && <Route path="/reports" element={<ReportingDashboard />} />}
        {/* Service Hub: one Workspace entry opens this shell, which renders a
            left sub-rail of sections with the active one in <Outlet/>. The four
            sections are the service-request lifecycle (catalogue -> requests ->
            approvals -> delegation). Mirrors the Admin sub-rail pattern. */}
        <Route path="/services" element={<ServiceHubLayout />}>
          <Route index element={<ServiceCatalog />} />
          <Route path="requests" element={<MyRequests />} />
          <Route path="approvals" element={<Approvals />} />
          <Route path="delegation" element={<MyDelegation />} />
        </Route>
        {/* Legacy top-level paths redirect into the hub so saved links still
            land (mirrors the /forms -> /services redirect from issue #4). */}
        <Route path="/requests" element={<Navigate to="/services/requests" replace />} />
        <Route path="/approvals" element={<Navigate to="/services/approvals" replace />} />
        <Route path="/me/delegation" element={<Navigate to="/services/delegation" replace />} />
        {/* Online Forms consolidated into the Service Catalog (issue #4):
            the bare list redirects so any saved /forms link still lands. The
            focused submit view stays full-width, outside the hub rail. */}
        <Route path="/forms" element={<Navigate to="/services" replace />} />
        <Route path="/forms/:id" element={<FormSubmit />} />
        {features.trust && <Route path="/trust" element={<Trust />} />}
        {/* Feedback moved into Settings → Help & Support; the old standalone
            page redirects so any saved /feedback link still lands. */}
        <Route path="/feedback" element={<Navigate to="/me/preferences" replace />} />
        {/* Knowledge Base: a Workspace entry opens this shell with a left
            sub-rail (Browse / Categories / Saved / Recently viewed); the active
            section renders in <Outlet/>. The article reader is a full-width
            sibling at /kb/article/:id (mirrors /forms/:id vs the hub). */}
        {features.kb && (
          <Route path="/kb" element={<KbLayout />}>
            <Route index element={<KbBrowse />} />
            <Route path="categories" element={<KbCategories />} />
            <Route path="category/:category" element={<KbCategory />} />
            <Route path="saved" element={<KbSaved />} />
            <Route path="recent" element={<KbRecent />} />
            <Route path="search" element={<KbSearch />} />
          </Route>
        )}
        {features.kb && <Route path="/kb/article/:id" element={<KBArticle />} />}
        <Route path="/me/preferences" element={<MePreferences />} />
        {/* Admin area: a single "Admin" sidebar item opens this shell, which
            renders a left sub-rail of sections with the active one in <Outlet/>.
            The five list pages live under the layout; focused editors below
            render full-width (no rail). Route paths are unchanged. */}
        <Route path="/admin" element={<AdminLayout />}>
          <Route index element={<AdminHome />} />
          <Route
            path="clients"
            element={
              <AdminAccessGate allow={isIquestAdmin} blurb={CUSTOMERS_BLURB}>
                <AdminClients />
              </AdminAccessGate>
            }
          />
          <Route path="announcements" element={<AdminAnnouncements />} />
          <Route path="forms" element={<AdminForms />} />
          {features.kb && (
            <Route
              path="kb"
              element={
                <AdminAccessGate allow={canAuthorKb} blurb={KB_ADMIN_BLURB}>
                  <AdminKB />
                </AdminAccessGate>
              }
            />
          )}
          <Route path="users" element={<AdminUsers />} />
          <Route path="view-as" element={<AdminViewAs />} />
        </Route>
        {/* Full-width editors / detail — siblings, no rail. v6 specificity routing
            keeps these ahead of the layout's list children. */}
        {/* Status Alerts composer: full-page, no rail (Implementation Spec §5). */}
        <Route path="/admin/announcements/new" element={<AdminStatusAlertComposer />} />
        <Route path="/admin/announcements/:id" element={<AdminStatusAlertComposer />} />
        <Route path="/admin/forms/:id/edit" element={<AdminFormEdit />} />
        {features.kb && (
          <Route
            path="/admin/kb/new"
            element={
              <AdminAccessGate allow={canAuthorKb} blurb={KB_ADMIN_BLURB}>
                <AdminKBEdit />
              </AdminAccessGate>
            }
          />
        )}
        {features.kb && (
          <Route
            path="/admin/kb/:id/edit"
            element={
              <AdminAccessGate allow={canAuthorKb} blurb={KB_ADMIN_BLURB}>
                <AdminKBEdit />
              </AdminAccessGate>
            }
          />
        )}
        {/* Onboarding wizard. Static segment, so it always beats /clients/:id. */}
        <Route
          path="/admin/clients/new"
          element={
            <AdminAccessGate allow={isIquestAdmin} blurb={CUSTOMERS_BLURB}>
              <AdminClientNew />
            </AdminAccessGate>
          }
        />
        {/* One customer, everything in one place. The section lives in the URL
            so back works and a link lands on the right card; an unknown section
            redirects to the customer's Overview. */}
        <Route path="/admin/clients/:id" element={<AdminCustomer />} />
        <Route path="/admin/clients/:id/:section" element={<AdminCustomer />} />
        {/* Legacy standalone matrix route: approval routing now lives inside the
            customer's Forms & approvals section. */}
        <Route
          path="/admin/clients/:companyId/approvals"
          element={<RedirectClientApprovals />}
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AppShell>
  );
}

/** Legacy /admin/clients/:companyId/approvals -> the customer's Forms &
 *  approvals section, which is where the matrix now lives. */
function RedirectClientApprovals() {
  const { companyId } = useParams<{ companyId: string }>();
  return <Navigate to={`/admin/clients/${companyId ?? ""}/forms`} replace />;
}
